import React from "react";
import { FeatherInfo } from "@subframe/core";
import { storedGstEnabledFrom } from "../../utils/gstApplicability";

interface GstPreRegistrationNoticeProps {
  /** The document's own date, `YYYY-MM-DD`. */
  documentDate?: string | null;
  /** "invoice", "bill", "credit note", "debit note" — used in the sentence. */
  documentLabel?: string;
  /** Overrides the date from the stored company (rarely needed). */
  gstEnabledFrom?: string | null;
}

/**
 * ZOUR-19 — why this document has no tax on it.
 *
 * Shown on a review or create page whenever the document predates the
 * company's GST registration. Without it, the tax columns and the ITC picker
 * simply are not there and the reviewer is left to work out whether that is
 * correct, a bug, or something they broke.
 *
 * Deliberately NOT a warning colour. Nothing is wrong: this is the right
 * treatment for a supply that happened before the client was registered. The
 * amber/red states on these pages mean "look at this, it may be incorrect",
 * and spending one of them here would train people to ignore them.
 *
 * ONE SENTENCE, on purpose (item 29). It used to carry a second line telling
 * the reader how to change the date or correct the registration. Both are
 * obvious from the page they are already on — the date field is right there,
 * and Settings is in the nav — so the line was instruction nobody needed on a
 * notice whose whole job is to explain, in passing, why the tax fields are
 * missing.
 *
 * The cockpit-core pipeline raises a matching `gst_not_applicable_before_date`
 * validation flag with the same sentence, but only after AI detection has run
 * on the document. This notice is derived from the date in front of the user,
 * so it is right immediately and it is right on a manual document that has no
 * validation results at all.
 */
export const GstPreRegistrationNotice: React.FC<GstPreRegistrationNoticeProps> = ({
  documentDate,
  documentLabel = "document",
  gstEnabledFrom,
}) => {
  const from = gstEnabledFrom ?? storedGstEnabledFrom();

  // ZOUR-19 review 11: input tax credit is a PURCHASE-side concept. Saying
  // "no input tax credit can be claimed" on a sales document is simply wrong,
  // and wrong tax copy on a CA's screen costs more trust than it saves words.
  //
  // WHICH SIDE IS WHICH IS NOT OBVIOUS HERE, and I got it wrong first time.
  // In this codebase a DEBIT NOTE is raised against an INVOICE
  // (`reference_invoice_id`) and pushed through Zoho's invoices endpoint — it
  // is a supplementary sales document, not a purchase one. A VENDOR CREDIT is
  // the purchase-side note (it is raised against a bill). So:
  //   purchase side: bill, vendor credit   → input tax credit
  //   sales side:    invoice, credit note, debit note → GST charged
  const isPurchaseSide =
    documentLabel === "bill" || documentLabel === "vendor credit";
  const consequence = isPurchaseSide
    ? "tax rates are hidden, no input tax credit can be claimed, and it will be pushed to your books with no tax"
    : "tax rates are hidden, no GST is charged, and it will be pushed to your books with no tax";

  return (
    <div className="flex w-full items-start gap-2 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
      <FeatherInfo className="mt-0.5 shrink-0 text-body font-body text-subtext-color" />
      <div className="flex flex-col gap-1">
        <span className="text-body-bold font-body-bold text-default-font">
          No GST on this {documentLabel}
        </span>
        <span className="text-body font-body text-subtext-color">
          {from
            ? `Your business became registered for GST on ${from}, and this ${documentLabel} is dated ${
                documentDate || "earlier"
              }. GST does not apply to it: ${consequence}.`
            : `GST does not apply to this ${documentLabel}, so ${consequence}.`}
        </span>
      </div>
    </div>
  );
};
